import * as React from 'react';
import { EGDSSheetPositionTypes } from '../types/index.esm.js';

/* Collection of utilities used by Sheets */
const sheetStack = [];
let currentIndex = -1;
// Returns the number of open sheets
const getOpenSheetCount = () => sheetStack.length;
// Returns the current sheet element (last one in the stack)
const getCurrentSheet = () => sheetStack[sheetStack.length - 1] || null;
const addSheetToStack = (sheetRef, props) => {
  // Check if this sheetRef is already in the stack
  if (!sheetStack.some(item => item.ref === sheetRef)) {
    sheetStack.push({
      ref: sheetRef,
      propsRef: {
        current: props
      }
    });
  }
};
const removeSheetFromStack = sheetRef => {
  // Removes specific sheet ref
  const index = sheetStack.findIndex(item => item.ref === sheetRef);
  if (index !== -1) {
    sheetStack.splice(index, 1);
  }
};
const setFocus = ref => {
  if (ref && ref.current) {
    ref.current.focus();
  }
};
const setFocusableElems = sheetRef => {
  if (sheetRef?.current) {
    return Array.from(sheetRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
  }
  return [];
};
const setElementFocus = (sheetRef, hasModal, focusElemRef) => {
  const focusable = setFocusableElems(sheetRef);
  if (focusElemRef?.current) {
    focusElemRef.current.focus();
  } else if (focusable.length) {
    focusable[hasModal ? 1 : 0]?.focus();
  } else {
    setFocus(sheetRef);
  }
};
const trapUpperBoundFocus = sheetRef => {
  const focusable = setFocusableElems(sheetRef);
  if (focusable.length > 1) {
    focusable[focusable.length - 2].focus(); // Send focus to last element before boundary
  }
};
const trapLowerBoundFocus = sheetRef => {
  const focusable = setFocusableElems(sheetRef);
  if (focusable.length > 1) {
    focusable[1].focus(); // Send focus to first element after boundary
  }
};
const defineSheetLayout = children => {
  const childrenArray = React.Children.toArray(children);
  const sheetLayoutObj = {
    get hasCloseButton() {
      return !!this.closeButtonElem;
    },
    get hasAlignment() {
      return !!this.hasCloseButton && (this.closeButtonElem?.props?.alignment ?? 'right');
    },
    get layoutProps() {
      return this.hasCloseButton && {
        space: 'two',
        direction: this.hasAlignment === 'top' ? 'column' : 'row-reverse'
      };
    },
    get closeButtonLayoutProps() {
      return this.hasCloseButton && this.hasAlignment === 'top' && {
        alignSelf: 'end'
      };
    },
    closeButtonElem: childrenArray.find(child => child.type?.displayName?.match(/(EGDS|Uitk)SheetCloseButton/)),
    footerElem: childrenArray.find(child => child.type?.displayName?.match(/(EGDS|Uitk)SheetFooter/)),
    childrenElem: childrenArray.filter(child => {
      if (child.type?.displayName) {
        // Regular expression to match "Uitk" or "EGDS" followed by "SheetCloseButton" or "SheetFooter"
        const sheetElemRegex = /^(EGDS|Uitk)(SheetCloseButton|SheetFooter)$/;
        return !sheetElemRegex.test(child.type.displayName);
      }
      return true; // This returns all the other elements
    })
  };
  return sheetLayoutObj;
};
const isDescendant = (parent, child) => {
  /**
   * Checks if the clear button was clicked on the body.
   * This was added because there's an edge case while using pwa in which the button is not treated as a descendant of the sheet.
   * Making the sheet close when clicking the clear button.
   */
  const closestButton = child?.closest('button');
  const isClearButtonFallback = closestButton && closestButton?.className.includes('clear');
  return !!parent?.current?.contains(child) || isClearButtonFallback;
};
const onBodyClick = e => {
  const {
    disableBodyClickHandler,
    triggerRef,
    onDismiss
  } = getCurrentSheet()?.propsRef?.current ?? {};
  let {
    target
  } = e;
  const sheetElem = getCurrentSheet()?.ref;
  /**
   * Checks if the sheet is within a shadow Dom and if so, sets the target to the composed path rather than the root.
   * This was added because there was an edge case where the sheet is within a shadow dom and would open and dismiss on the same click.
   */
  if (e.target.shadowRoot) {
    target = e.composedPath()[0];
  }
  if (triggerRef?.current === target || disableBodyClickHandler || isDescendant(sheetElem, target) || isDescendant(triggerRef, target)) {
    return;
  }
  onDismiss && onDismiss();
};
const onKeyDown = e => {
  const {
    disableArrowKeyHandler,
    onDismiss
  } = getCurrentSheet()?.propsRef?.current ?? {};
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    if (disableArrowKeyHandler) {
      return;
    }
    const target = document.activeElement;
    // Don't prevent default if the active element is a select, or number input
    if (target instanceof HTMLSelectElement || target instanceof HTMLInputElement && target.type === 'number') {
      return;
    }
    e.preventDefault();
  }
  if (e.key !== 'Escape') {
    return;
  }
  onDismiss && onDismiss();
};
const onKeyUp = e => {
  const sheetElem = getCurrentSheet()?.ref;
  const focusable = setFocusableElems(sheetElem);
  const {
    disableArrowKeyHandler,
    onDismiss,
    triggerRef
  } = getCurrentSheet()?.propsRef?.current ?? {};
  const parent = sheetElem;
  let target;
  if (e.shiftKey && e.key === 'Tab') {
    target = document.activeElement;
    if (triggerRef?.current === e.target) {
      return false;
    }
  }
  if (parent.current) {
    setFocusableElems(sheetElem);
    if (e.key === 'ArrowDown') {
      if (disableArrowKeyHandler) {
        return;
      }
      target = document.activeElement;
      if (triggerRef?.current === e.target) {
        currentIndex = 0;
        focusable[currentIndex].focus();
        return;
      }
      const elemToFocusOn = focusable[currentIndex + 1];
      if (elemToFocusOn) {
        currentIndex += 1;
        elemToFocusOn.focus();
      }
    } else if (e.key === 'ArrowUp') {
      if (disableArrowKeyHandler) {
        return;
      }
      target = document.activeElement;
      if (triggerRef?.current === e.target) {
        return false;
      }
      const elemToFocusOn = focusable[currentIndex - 1];
      if (elemToFocusOn) {
        currentIndex -= 1;
        elemToFocusOn.focus();
      }
    } else if (e.key === 'Tab') {
      target = document.activeElement;
      /**
       * Checks if the component is within a shadow dom and if so, sets the target to the composed path rather than the root.
       * This was added because there was an edge case where the sheet used for the calendar is within a shadow dom and would open and dismiss while using keyboard navigation.
       */
      if (e.target.shadowRoot) {
        target = e.composedPath()[0];
      }
      for (let i = 0; i <= focusable.length; i++) {
        if (target === focusable[i]) {
          currentIndex = i;
        }
      }
      const isElemDescendant = isDescendant(parent, target);
      if (!isElemDescendant) {
        onDismiss && onDismiss();
      }
    }
  }
};
const attachHandlers = (props, sheetRef) => {
  const {
    body
  } = document;
  addSheetToStack(sheetRef, props);
  // Prevents event listeners from being attached multiple times
  if (getOpenSheetCount() === 1) {
    body.addEventListener('click', onBodyClick, {
      capture: true
    });
    body.addEventListener('keydown', onKeyDown);
    body.addEventListener('keyup', onKeyUp);
  }
};
const detachHandlers = sheetRef => {
  const {
    body
  } = document;
  // Removes specific sheet ref
  removeSheetFromStack(sheetRef);
  // Prevents event listeners from being detached when there are multiple sheets open
  if (getOpenSheetCount() === 0) {
    currentIndex = -1; // Resets the currentIndex back to -1 upon closing
    body.removeEventListener('click', onBodyClick, {
      capture: true
    });
    body.removeEventListener('keydown', onKeyDown);
    body.removeEventListener('keyup', onKeyUp);
  }
};
// Updates the current props of a sheet that is already tracked in sheetStack
const updateSheetProps = (sheetRef, newProps) => {
  const sheetInstance = sheetStack.find(sheet => sheet.ref === sheetRef);
  if (sheetInstance) {
    sheetInstance.propsRef.current = newProps;
  }
};
const isValidEGDSSheetPosition = value => Object.values(EGDSSheetPositionTypes).includes(value);
/**
 * Add or remove the "has sheet" class name to the body element
 *
 * @param action 'add' | 'remove'
 * @returns target.add(className) || target.remove(className)
 */
const hasSheetClassName = action => {
  const className = 'has-uitk-sheet';
  const target = document.body.classList;
  // Do not remove if there are still modal sheets, centered, or full sheets open
  if (action === 'remove' && sheetStack.some(sheet => sheet.propsRef?.current.hasModal || sheet.propRefs?.current.type === 'centered' || sheet.propRefs?.current.type === 'full')) {
    return;
  }
  return action === 'add' ? target.add(className) : target.remove(className);
};
export { addSheetToStack, attachHandlers, defineSheetLayout, detachHandlers, hasSheetClassName, isValidEGDSSheetPosition, onBodyClick, onKeyDown, onKeyUp, removeSheetFromStack, setElementFocus, setFocus, setFocusableElems, trapLowerBoundFocus, trapUpperBoundFocus, updateSheetProps };